import { useFocusEffect } from "expo-router";
import { useCallback, useRef } from "react";
import type { LayoutChangeEvent, ScrollView } from "react-native";

/** Last scroll offset per screen, kept for the life of the app session. */
const offsets = new Map<string, number>();
/** Give up restoring if the content never grows tall enough within this. */
const RESTORE_WINDOW_MS = 3000;
export const clearScrollOffsets = () => offsets.clear();

/**
 * Puts a ScrollView back where the reader left it: after the screen remounts
 * (switching tabs), and when it comes back into focus. The offset is applied
 * once the content is tall enough to hold it, so a list still rendering from
 * the cache is not clamped to its top.
 */
export function useScrollRestoration(key: string) {
  const ref = useRef<ScrollView>(null);
  const target = useRef<number | null>(offsets.get(key) ?? null);
  const armedAt = useRef(Date.now());
  const viewport = useRef(0);
  const contentHeight = useRef(0);
  const keyRef = useRef(key);

  if (keyRef.current !== key) {
    keyRef.current = key;
    target.current = offsets.get(key) ?? null;
    armedAt.current = Date.now();
  }

  const tryRestore = useCallback(() => {
    const y = target.current;
    if (y == null || !ref.current) return;
    if (y <= 0) {
      target.current = null;
      return;
    }
    if (!contentHeight.current) return;
    if (contentHeight.current < y + viewport.current) {
      if (Date.now() - armedAt.current < RESTORE_WINDOW_MS) return;
    }
    ref.current.scrollTo({ y, animated: false });
    target.current = null;
  }, []);

  useFocusEffect(
    useCallback(() => {
      target.current = offsets.get(keyRef.current) ?? null;
      armedAt.current = Date.now();
      tryRestore();
    }, [tryRestore]),
  );

  const onScrollY = useCallback((y: number) => {
    // Ignore the scroll events of an unrestored mount: they would overwrite the
    // saved offset with 0 before it has been applied.
    if (target.current != null) {
      if (Date.now() - armedAt.current < RESTORE_WINDOW_MS) return;
      target.current = null;
    }
    offsets.set(keyRef.current, y);
  }, []);

  const onContentSizeChange = useCallback(
    (_w: number, h: number) => {
      contentHeight.current = h;
      tryRestore();
    },
    [tryRestore],
  );

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    viewport.current = event.nativeEvent.layout.height;
  }, []);

  /** Scroll somewhere on purpose (e.g. to the top for new posts). */
  const scrollTo = useCallback((y: number, animated = true) => {
    target.current = null;
    offsets.set(keyRef.current, y);
    ref.current?.scrollTo({ y, animated });
  }, []);

  return { ref, onScrollY, onContentSizeChange, onLayout, scrollTo };
}
